/**
 * Hook para ler/atualizar a configuração de pro-rata do tenant.
 *
 * RELEASE: 2026-06-17 v1.1.0 - Adiciona campos da Fase B (mid-cycle):
 *   apply_on_subscription_edit, subscription_edit_mode, min_proration_amount.
 * RELEASE: 2026-06-16 v1.0.0 - Versão inicial.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

export type ProRataMode =
  | 'none'
  | 'charge_immediately'
  | 'add_to_next_invoice'
  | 'create_credit_note';

// Modo do ajuste mid-cycle (edição de recorrência ativa).
// 'inherit' = usa o mesmo modo geral (mode).
export type SubscriptionEditMode =
  | 'inherit'
  | 'charge_immediately'
  | 'add_to_next_invoice'
  | 'none';

// Política de prompt para consumo de crédito do cliente em pro-rata mid-cycle
// 'auto' = consome silenciosamente quando habilitado
// 'ask'  = exige confirmação explícita do usuário no momento de salvar
export type CustomerCreditPromptMode = 'auto' | 'ask';

export interface ProRataConfig {
  enabled: boolean;
  mode: ProRataMode;
  apply_on_create: boolean;
  apply_on_plan_change: boolean;
  rounding?: string;
  // v1.1.0 — mid-cycle (Fase B)
  apply_on_subscription_edit: boolean;
  subscription_edit_mode: SubscriptionEditMode;
  min_proration_amount: number;
  // v1.2.0 — Padrões de cobrança (UI prefill apenas, não aplicado no backend)
  charge_juros_enabled?: boolean;
  charge_juros?: {
    multa_tipo?: 'percent' | 'fixed';
    multa_valor?: number;
    juros_tipo?: 'percent' | 'fixed';
    juros_valor?: number;
  };
  charge_expiracao_enabled?: boolean;
  charge_dias_expiracao?: number;
  // v1.3.0 — Política de consumo automático de crédito do cliente
  // (enforced no backend: update_financeiro_assinatura v1.11.0)
  auto_apply_customer_credit?: boolean;
  customer_credit_prompt_mode?: CustomerCreditPromptMode;
  // v2026-06-27 — card_installment_payment_mode foi MIGRADO para a tabela
  // financeiro_conta_corrente (regra é por provider, não por tenant).
  updated_at?: string;
  updated_by?: string | null;
}


export function useFinancialSettings() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['tenant-pro-rata-config', tenantId],
    enabled: !!tenantId,
    staleTime: 30_000,
    queryFn: async (): Promise<ProRataConfig> => {
      const { data, error } = await supabase.rpc(
        'get_tenant_pro_rata_config' as never,
        { p_tenant_id: tenantId! } as never,
      );
      if (error) throw error;
      return (data ?? {}) as ProRataConfig;
    },
  });

  const mutation = useMutation({
    mutationFn: async (config: Partial<ProRataConfig>) => {
      const { data, error } = await supabase.rpc(
        'update_tenant_pro_rata_config' as never,
        { p_config: config } as never,
      );
      if (error) throw error;
      return data as ProRataConfig;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['tenant-pro-rata-config', tenantId], data);
      toast.success('Configuração de pro-rata atualizada');
    },
    onError: (err: any) => {
      toast.error(err?.message ?? 'Falha ao salvar configuração');
    },
  });

  return {
    config: query.data,
    isLoading: query.isLoading,
    isSaving: mutation.isPending,
    save: mutation.mutateAsync,
    refetch: query.refetch,
  };
}
